import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type HideableSection = 'leerhuis' | 'vrije-diensten' | 'rapportages';

interface UIPrefsContextType {
  hiddenSections: HideableSection[];
  isSectionHidden: (section: HideableSection) => boolean;
  toggleSection: (section: HideableSection) => void;
}

const STORAGE_KEY = 'kerkplanner_hidden_sections';

function loadHiddenSections(): HideableSection[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as HideableSection[];
  } catch {
    return [];
  }
}

function saveHiddenSections(sections: HideableSection[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
  } catch {
    // ignore
  }
}

const UIPrefsContext = createContext<UIPrefsContextType | null>(null);

export function UIPrefsProvider({ children }: { children: ReactNode }) {
  const [hiddenSections, setHiddenSections] = useState<HideableSection[]>(loadHiddenSections);

  const isSectionHidden = useCallback(
    (section: HideableSection) => hiddenSections.includes(section),
    [hiddenSections]
  );

  const toggleSection = useCallback((section: HideableSection) => {
    setHiddenSections(prev => {
      const next = prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section];
      saveHiddenSections(next);
      return next;
    });
  }, []);

  return (
    <UIPrefsContext.Provider value={{ hiddenSections, isSectionHidden, toggleSection }}>
      {children}
    </UIPrefsContext.Provider>
  );
}

export function useUIPrefs() {
  const ctx = useContext(UIPrefsContext);
  if (!ctx) throw new Error('useUIPrefs must be used within UIPrefsProvider');
  return ctx;
}
